import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateSourceInput,
  UpdateSourceInput,
  SourcesQuery,
  SourceSortField,
} from '../model/contracts'
import {
  fetchSources,
  createSource,
  updateSource,
  deleteSource,
} from '../api/sourcesClient'
import { useAuth } from '../../auth/state/useAuth'

export function useSourcesController() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const createdBy = user?.id ? String(user.id) : undefined

  const [query, setQuery] = useState<SourcesQuery>({
    page: 1,
    limit: 20,
    platform: 'all',
    isEnabled: null,
    search: '',
    sort: '-createdAt',
    createdBy,
  })

  const effectiveQuery = { ...query, createdBy }
  const queryKey = ['sources', effectiveQuery]

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchSources(effectiveQuery, signal),
  })

  const onSearchChange = useCallback((search: string) => {
    setQuery((prev) => ({ ...prev, search, page: 1 }))
  }, [])

  const onPlatformChange = useCallback((platform: string) => {
    setQuery((prev) => ({ ...prev, platform, page: 1 }))
  }, [])

  const onIsEnabledChange = useCallback((value: string) => {
    setQuery((prev) => ({
      ...prev,
      isEnabled: value === 'all' ? null : value === 'true',
      page: 1,
    }))
  }, [])

  const onSortChange = useCallback((sortBy: SourceSortField) => {
    setQuery((prev) => {
      const currentSort = prev.sort || ''
      const isDesc = currentSort.startsWith('-')
      const currentField = isDesc
        ? currentSort.slice(1)
        : currentSort.startsWith('+')
          ? currentSort.slice(1)
          : currentSort

      let nextSort = sortBy as string
      if (currentField === sortBy) {
        // Toggle: asc -> desc | desc -> asc
        nextSort = isDesc ? `+${sortBy}` : `-${sortBy}`
      } else {
        // New field: default to asc (or desc if preferred, I'll go with asc)
        nextSort = `+${sortBy}`
      }

      return { ...prev, sort: nextSort, page: 1 }
    })
  }, [])

  const onPageChange = useCallback((page: number) => {
    setQuery((prev) => ({ ...prev, page }))
  }, [])

  const createMutation = useMutation({
    mutationFn: (input: CreateSourceInput) => createSource(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (input: UpdateSourceInput) => updateSource(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] })
    },
  })

  return {
    sources: data?.sources ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? query.page,
    limit: data?.limit ?? query.limit,
    query,
    isLoading: isLoading || isPlaceholderData,
    error,
    onSearchChange,
    onPlatformChange,
    onIsEnabledChange,
    onSortChange,
    onPageChange,
    createSource: createMutation.mutate,
    updateSource: updateMutation.mutate,
    deleteSource: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
